import {
  Accordion,
  Box,
  Button,
  StarRating,
  Text,
  Link,
  useMediaQuery,
} from '@lounge-fe/ui-kit'
import { useReviewsQuery } from './useReviewsQuery'
import Review from './Review'
import { ReviewsResponse, type Review as IReview } from './types'
import { ReviewsProps } from './types'
import { ReviewsModal } from './ReviewsModal'
import { t } from 'i18next'
import { ComponentProps, ReactNode, useMemo, useState } from 'react'
import { Trigger } from '@lounge-fe/ui-kit/src/components/Popover/Trigger'

const ReviewList = ({ reviews }: { reviews: IReview[] }) => {
  const [showOriginal, setShowOriginal] = useState<Record<number, boolean>>({})

  const handleToggleOriginal = (id: number) => {
    return () => {
      setShowOriginal((prev) => ({
        ...prev,
        [id]: !prev[id],
      }))
    }
  }

  return (
    <>
      {reviews.map(
        ({
          reviewer: { author, isVerified },
          title,
          dateCreated,
          rating,
          body,
          id,
          original,
        }) => (
          <Review key={id}>
            <Review.Details
              rating={rating}
              title={showOriginal[id] && original ? original.title : title}
              verified={isVerified}
            />
            <Review.Description>
              {showOriginal[id] && original ? original.body : body}
            </Review.Description>
            <Review.Cite
              date={dateCreated}
              author={author}
              {...(original && {
                original,
                showOriginal: showOriginal[id],
                onToggleOriginal: handleToggleOriginal(id),
              })}
            />
          </Review>
        ),
      )}
    </>
  )
}

const Reviews = ({
  productId,
  onClick,
  isInitialOpen,
  variant = 'accordion',
  avgRating,
  reviewCount,
}: ReviewsProps) => {
  const isDesktop = useMediaQuery(
    ({ screens }) => `(min-width: ${screens['sm']})`,
  )

  const {
    data: { pages = [] } = {},
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useReviewsQuery({
    productId,
    targetPageSize: 24,
    getNextPageParam: (lastPage: ReviewsResponse) => lastPage.cursor,
    refetchOnWindowFocus: false,
    enabled: productId !== undefined,
  })

  const reviews = useMemo(() => {
    return pages.reduce<IReview[]>((acc, { reviews = [] }) => {
      return [...acc, ...reviews]
    }, [])
  }, [pages])

  const averageRating = useMemo(() => {
    return avgRating ?? 0
  }, [avgRating, reviews])

  if (averageRating === 0) {
    return null
  }

  const totalReviews = reviewCount

  const accordionTrigger: ComponentProps<typeof Trigger>['children'] = (
    <Link
      as="button"
      className="w-full mt-2 mb-4 underline typography--body-sm text-left"
    >
      {t('product.reviews.read_more')}
    </Link>
  )

  const productInfoTrigger: ComponentProps<typeof Trigger>['children'] = (
    <Link
      as="button"
      className="flex flex-row align-center items-center gap-2 mb-4 mt-4"
    >
      {averageRating !== undefined && (
        <StarRating maxStars={5} stars={averageRating} />
      )}{' '}
      <Text variant="body-sm" className="underline">
        {totalReviews} {t('general.reviews')}
      </Text>
    </Link>
  )

  if (variant === 'productInfo') {
    return (
      <ReviewsModal trigger={productInfoTrigger}>
        <ReviewList reviews={reviews} />
        <Box className="text-center my-6">
          <Text variant="body-sm" className="mb-2">
            {t('product.reviews.showing', {
              count: reviews.length,
              total: totalReviews,
            })}
          </Text>
          {hasNextPage && (
            <Button onClick={() => fetchNextPage()} isLoading={isFetching}>
              {t('action.load_more')}
            </Button>
          )}
        </Box>
      </ReviewsModal>
    )
  }

  return (
    <Accordion.Item
      onClick={onClick}
      initialOpen={isInitialOpen}
      title={
        <div className="flex justify-start gap-3 w-full">
          <Text variant="title-six">
            {t('general.reviews')} ({reviewCount})
          </Text>
          {averageRating && !isDesktop && (
            <StarRating maxStars={5} stars={averageRating} />
          )}
        </div>
      }
    >
      <ReviewList reviews={reviews.slice(0, 2)} />

      {(hasNextPage || reviews.length > 0) && (
        <ReviewsModal trigger={accordionTrigger}>
          <ReviewList reviews={reviews} />
          <Box className="text-center my-6">
            <Text variant="body-sm" className="mb-2">
              {t('product.reviews.showing', {
                count: reviews.length,
                total: reviewCount,
              })}
            </Text>
            {hasNextPage && (
              <Button onClick={() => fetchNextPage()} isLoading={isFetching}>
                {t('action.load_more')}
              </Button>
            )}
          </Box>
        </ReviewsModal>
      )}
    </Accordion.Item>
  )
}

export { Reviews }
